import React, { useState } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ResponsiveContainer,
  Legend,
} from "recharts";
import styles from "./GraficoParceiro.module.css";
import AbrirClienteParceiro from "./AbrirClienteParceiro";

const consuladosNomes = {
  54: "Brasilia",
  55: "Rio de Janeiro",
  56: "Sao Paulo",
  57: "Recife",
  128: "Porto Alegre",
};

export const processarVendasPorDia = (data, intervalo = 180) => {
  const hoje = new Date();
  hoje.setHours(0, 0, 0, 0);

  return data
    .filter((item) => {
      const dataFechamento = new Date(item.dataFechamento);
      dataFechamento.setHours(0, 0, 0, 0);
      const diferencaDias = (hoje - dataFechamento) / (1000 * 60 * 60 * 24);
      return diferencaDias <= intervalo;
    })
    .reduce((dias, item) => {
      const data = new Date(item.dataFechamento).toISOString().split("T")[0];
      dias.set(data, (dias.get(data) || 0) + 1);
      return dias;
    }, new Map());
};

const ordenarVendasPorDia = (data, intervalo) =>
  Array.from(processarVendasPorDia(data, intervalo), ([data, quantidade]) => ({ data, quantidade }))
    .sort((a, b) => a.data.localeCompare(b.data));

const GraficoParceiros = ({
  rawData,
  intervaloDefault = 60,
  isAdmin = false,
}) => {
  const [rangeDateSells, setRangeDateSells] = useState(intervaloDefault);

  if (!rawData || rawData.length === 0)
    return (
      <p className={styles.emptyChart}>
        Nenhum dado disponível para o gráfico.
      </p>
    );

  console.log("Dados brutos:", rawData);
  const dadosFormatados = ordenarVendasPorDia(rawData, rangeDateSells);
  console.log("Vendas individuais para o gráfico:", dadosFormatados);


  return (
    <>
      <div className={styles.chartToolbar}>
        <label htmlFor="partner-history-period">Selecionar período:</label>
        <select
          id="partner-history-period"
          value={rangeDateSells}
          onChange={(e) => setRangeDateSells(parseInt(e.target.value))}
        >
          <option value={7}>Últimos 7 dias</option>
          <option value={30}>Últimos 30 dias</option>
          <option value={60}>Últimos 60 dias</option>
          <option value={180}>Últimos 180 dias</option>
        </select>
      </div>

      <div className={styles.chartCanvas}>
        <ResponsiveContainer>
          <BarChart data={dadosFormatados}>
            <CartesianGrid
              strokeDasharray="3 5"
              vertical={false}
              stroke="#e8edf4"
            />
            <XAxis
              axisLine={false}
              tickLine={false}
              tick={{ fill: "#718096", fontSize: 11 }}
              tickMargin={10}
              dataKey="data"
              tickFormatter={(tick) => tick.split("-").reverse().join("/")}
            />
            <YAxis
              allowDecimals={false}
              axisLine={false}
              tickLine={false}
              tick={{ fill: "#718096", fontSize: 11 }}
              width={45}
            />
            <Tooltip
              cursor={{ fill: "#f1f5fb" }}
              contentStyle={{
                borderRadius: 10,
                border: "1px solid #e2e8f1",
                fontSize: 12,
              }}
            />
            <Legend
              wrapperStyle={{ fontSize: 12, paddingTop: 12 }}
              iconType="circle"
              iconSize={8}
            />
            <Bar
              dataKey="quantidade"
              fill="#3d75bc"
              radius={[5, 5, 0, 0]}
              maxBarSize={44}
              name="Quantidade de vendas"
            />
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div className={styles.salesList}>
        <h3>Vendas (últimos {rangeDateSells} dias)</h3>

        {rawData
          .filter((item) => {
            const hoje = new Date();
            const dataVenda = new Date(item.dataFechamento);
            const diff = (hoje - dataVenda) / (1000 * 60 * 60 * 24);
            return diff <= rangeDateSells;
          })
          .sort(
            (a, b) => new Date(b.dataFechamento) - new Date(a.dataFechamento),
          )
          .map((item) => {
            const nomeLimitado =
              item.clienteNome.length > 10
                ? item.clienteNome.slice(0, 10) + "***"
                : item.clienteNome;
            const consuladoNome = consuladosNomes[Number(item.consulado)];
            const aprovados = Number(item.resultados?.aprovados) || 0;
            const negados = Number(item.resultados?.negados) || 0;
            const temResultados = aprovados > 0 || negados > 0;

            return (
              <div
                key={item.id}
                className={`${styles.saleRow} ${temResultados ? styles.withResults : ""}`}
              >
                <div>
                  <span>Data</span>
                  <strong>{item.dataFechamento.split("T")[0]}</strong>
                  {consuladoNome && (
                    <span className={styles.clientDetail}>
                      Consulado: {consuladoNome}
                    </span>
                  )}
                </div>
                <div>
                  <span>Cliente</span>
                  <strong>{nomeLimitado}</strong>
                  <span className={styles.clientDetail}>
                    {item.clienteDDD
                      ? `DDD (${item.clienteDDD})`
                      : "DDD não informado"}
                  </span>
                </div>

                <div>
                  <span>Parceiro</span>
                  <strong>{item.usuarioParceiro}</strong>
                  {isAdmin && <AbrirClienteParceiro vendaId={item.id} />}
                </div>
                {temResultados && (
                  <div
                    className={styles.results}
                    aria-label="Resultados do cliente"
                  >
                    <span>Resultado</span>
                    <div className={styles.badges}>
                      {aprovados > 0 && (
                        <span className={`${styles.badge} ${styles.approved}`}>
                          {aprovados === 1
                            ? "Visto Aprovado!"
                            : `${aprovados} vistos aprovados`}
                        </span>
                      )}
                      {negados > 0 && (
                        <span className={`${styles.badge} ${styles.denied}`}>
                          {negados === 1
                            ? "Visto Negado"
                            : `${negados} vistos negados`}
                        </span>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
      </div>
    </>
  );
};

export default GraficoParceiros;
